/**
 * Surface Registry
 * Phase 2: Module Loader Integration
 * Manages surface definitions and navigation with dynamic module loading
 */

import * as React from 'react';
import { EventBus } from './events/EventBus';
import { ModuleLoader } from './modules/ModuleLoader';
import type { SurfaceDefinition } from './surfaces/types';

// Global surface registry instance
let globalSurfaceRegistry: SurfaceRegistry | null = null;

export const getGlobalSurfaceRegistry = (): SurfaceRegistry => {
  if (!globalSurfaceRegistry) {
    throw new Error('SurfaceRegistry not initialized. Call initGlobalSurfaceRegistry first.');
  }
  return globalSurfaceRegistry;
};

export const initGlobalSurfaceRegistry = (moduleLoader: ModuleLoader, eventBus: EventBus): SurfaceRegistry => {
  if (globalSurfaceRegistry) {
    return globalSurfaceRegistry;
  }
  globalSurfaceRegistry = new SurfaceRegistry(moduleLoader, eventBus);
  return globalSurfaceRegistry;
};

export class SurfaceRegistry {
  private surfaces: Map<string, SurfaceDefinition> = new Map();
  private moduleLoader: ModuleLoader;
  public eventBus: EventBus;

  constructor(moduleLoader: ModuleLoader, eventBus: EventBus) {
    this.moduleLoader = moduleLoader;
    this.eventBus = eventBus;

    // Listen for module loaded events to register surfaces
    this.eventBus.on('module:loaded', (data: any) => {
      this.registerModuleSurfaces(data.moduleName);
    });

    // Listen for module unloaded events to unregister surfaces
    this.eventBus.on('module:unloaded', (data: any) => {
      this.unregisterModuleSurfaces(data.moduleName);
    });
  }

  /**
   * Register surfaces from a module
   */
  public registerModuleSurfaces(moduleName: string): void {
    const module = this.moduleLoader.getModule(moduleName);
    if (!module) return;

    // Register workspace surfaces
    for (const surfaceName of module.manifest?.workspaceSurfaces || []) {
      const component = module.exports?.[surfaceName] || module.exports?.default;
      if (component) {
        this.registerSurface(surfaceName, component, moduleName, 'workspace');
      }
    }

    // Register peek surfaces
    for (const surfaceName of module.manifest?.peekSurfaces || []) {
      const component = module.exports?.[surfaceName] || module.exports?.default;
      if (component) {
        this.registerSurface(surfaceName, component, moduleName, 'peek');
      }
    }

    // Register modal surfaces
    for (const surfaceName of module.manifest?.modalSurfaces || []) {
      const component = module.exports?.[surfaceName] || module.exports?.default;
      if (component) {
        this.registerSurface(surfaceName, component, moduleName, 'modal');
      }
    }

    this.eventBus.emit('surface:registered', { moduleName });
  }

  /**
   * Unregister surfaces from a module
   */
  public unregisterModuleSurfaces(moduleName: string): void {
    const module = this.moduleLoader.getModule(moduleName);
    if (!module) return;

    // Unregister all surfaces from the module
    for (const surfaceName of module.manifest?.workspaceSurfaces || []) {
      this.unregisterSurface(surfaceName);
    }

    for (const surfaceName of module.manifest?.peekSurfaces || []) {
      this.unregisterSurface(surfaceName);
    }

    for (const surfaceName of module.manifest?.modalSurfaces || []) {
      this.unregisterSurface(surfaceName);
    }

    this.eventBus.emit('surface:unregistered', { moduleName });
  }
/**
 * Register a surface
 */
 public registerSurface(
  name: string,
  component: React.ComponentType<any>,
    moduleName: string,
    type: 'workspace' | 'peek' | 'modal'
  ): void {
    // Determine phase based on module name or use default
    const phase = this.getPhaseForModule(moduleName);

    const surface: SurfaceDefinition = {
      id: name,
      name: name,
      type,
      component,
      phase,
      isMock: true,
      path: this.getPathForSurface(name, type)
    };
    
    // Store the original component for later use with custom props
    surface.originalComponent = component;

    this.surfaces.set(name, surface);
  }

  /**
   * Unregister a surface
   */
  public unregisterSurface(name: string): void {
    this.surfaces.delete(name);
  }

  /**
   * Get a surface by name
   */
  getSurface(name: string): SurfaceDefinition | undefined {
    return this.surfaces.get(name);
  }

  /**
   * Get all surfaces
   */
  getAllSurfaces(): SurfaceDefinition[] {
    return Array.from(this.surfaces.values());
  }

  /**
   * Get surfaces by type
   */
  getSurfacesByType(type: 'workspace' | 'peek' | 'modal'): SurfaceDefinition[] {
    return Array.from(this.surfaces.values()).filter(s => s.type === type);
  }

  /**
   * Get surfaces by phase
   */
  getSurfacesByPhase(phase: number): SurfaceDefinition[] {
    return Array.from(this.surfaces.values()).filter(s => s.phase === phase);
  }

  /**
   * Get all surfaces (public method)
   */
  getAllSurfacesPublic(): SurfaceDefinition[] {
    return Array.from(this.surfaces.values());
  }

  /**
   * Get path for a surface based on type
   */
  private getPathForSurface(name: string, type: 'workspace' | 'peek' | 'modal'): string[] {
    const basePath = type === 'peek' ? 'Quick Note' : 'Home';

    switch (name) {
      case 'HomePage':
        return ['Home'];
      case 'PageEditor':
        return ['Home', 'Page Editor'];
      case 'DatabaseView':
        return ['Home', 'Database View'];
      case 'BlockPalette':
        return ['Home', 'Block Palette'];
      case 'BlockPreview':
        return ['Block Preview'];
      case 'AIChat':
        return ['AI Chat'];
      case 'QuickNote':
        return ['Quick Note'];
      default:
        return [basePath, name];
    }
  }

  /**
   * Get phase for a module
   */
  private getPhaseForModule(moduleName: string): number {
    const phaseMap: Record<string, number> = {
      'FreshCards': 1,
      'BlockModule': 3,
      'PageModule': 4,
      'DatabaseModule': 5,
      'AIModule': 7
    };
    return phaseMap[moduleName] || 1;
  }

}

// Legacy mock surfaces for backward compatibility
export const mockSurfaces: Record<string, SurfaceDefinition> = {};

// Navigation utilities
export const navigateToSurface = (surfaceName: string, params?: any) => {
  if (!globalSurfaceRegistry) {
    console.error('SurfaceRegistry not initialized');
    return null;
  }
  const surface = globalSurfaceRegistry.getSurface(surfaceName);
  if (!surface) {
    console.error(`Surface not found: ${surfaceName}`);
    return null;
  }

  // Emit navigation event for App.tsx to handle
  // We use a custom event name that won't conflict with module events
  if (globalSurfaceRegistry.eventBus) {
    globalSurfaceRegistry.eventBus.emit('app:navigate', {
      surface: surfaceName,
      params,
      phase: surface.phase,
      timestamp: new Date()
    });
  }

  console.log(`Navigating to: ${surfaceName} (Phase ${surface.phase})`);
  return surface;
};

export const getCurrentSurface = (): SurfaceDefinition | null => {
  // Phase 1+ will implement actual state management
  return null;
};

export const getAllSurfaces = (): SurfaceDefinition[] => {
  if (!globalSurfaceRegistry) {
    return [];
  }
  return globalSurfaceRegistry.getAllSurfaces();
};

export const getSurfaceByPhase = (phase: number): SurfaceDefinition[] => {
  if (!globalSurfaceRegistry) {
    return [];
  }
  return globalSurfaceRegistry.getSurfacesByPhase(phase);
};

export const isSurfaceMock = (surfaceName: string): boolean => {
  if (!globalSurfaceRegistry) {
    return true;
  }
  const surface = globalSurfaceRegistry.getSurface(surfaceName);
  return surface ? surface.isMock ?? true : true;
};
